import { useTimeFormat } from "@/lib/dateTime";
import { cn } from "@/lib/utils";

const pad = (n: number) => String(n).padStart(2, "0");
const selectClass = "h-9 rounded-md border border-input bg-card px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring";

/**
 * A time of day that follows the 12-hour / 24-hour setting. The value is always "HH:MM" in 24-hour form (what the
 * server stores), so only the way it is shown and picked changes. A native time box can't be used: the browser decides
 * its style from the computer's language, not from the CRM's setting.
 */
export function TimeInput({ value, onChange, label, id, className, compact }: { value: string; onChange: (v: string) => void; label: string; id?: string; className?: string; compact?: boolean }) {
  const format = useTimeFormat();
  const [h = 0, m = 0] = /^\d{2}:\d{2}$/.test(value) ? value.split(":").map(Number) : [];
  const set = (hour: number, minute: number) => onChange(`${pad(hour)}:${pad(minute)}`);
  const small = compact ? "h-8 text-xs" : "";

  const minutes = (
    <select aria-label={`${label}, minutes`} value={m} onChange={(e) => set(h, Number(e.target.value))} className={cn(selectClass, small, "font-mono")}>
      {Array.from({ length: 60 }, (_, i) => <option key={i} value={i}>{pad(i)}</option>)}
    </select>
  );

  if (format === "24h") {
    return (
      <div id={id} role="group" aria-label={label} className={cn("inline-flex items-center gap-1", className)}>
        <select aria-label={`${label}, hour`} value={h} onChange={(e) => set(Number(e.target.value), m)} className={cn(selectClass, small, "font-mono")}>
          {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{pad(i)}</option>)}
        </select>
        <span aria-hidden="true">:</span>
        {minutes}
      </div>
    );
  }

  const pm = h >= 12;
  const hr12 = h % 12 === 0 ? 12 : h % 12;
  const from12 = (hr: number, isPm: boolean) => (hr % 12) + (isPm ? 12 : 0);
  return (
    <div id={id} role="group" aria-label={label} className={cn("inline-flex items-center gap-1", className)}>
      <select aria-label={`${label}, hour`} value={hr12} onChange={(e) => set(from12(Number(e.target.value), pm), m)} className={cn(selectClass, small, "font-mono")}>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
      </select>
      <span aria-hidden="true">:</span>
      {minutes}
      <select aria-label={`${label}, am or pm`} value={pm ? "pm" : "am"} onChange={(e) => set(from12(hr12, e.target.value === "pm"), m)} className={cn(selectClass, small)}>
        <option value="am">am</option>
        <option value="pm">pm</option>
      </select>
    </div>
  );
}
